import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, signup as apiSignup, getCurrentUser, logout as apiLogout, SignupData } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; code?: number; message?: string }>;
  signup: (data: SignupData) => Promise<{ success: boolean; code?: number; message?: string }>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Try to get current user with httpOnly cookie
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        // No valid session, user remains null
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; code?: number; message?: string }> => {
    const response = await apiLogin({ email, password });
    if (response.code === 200 && (response.success || response.user)) {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch {
        setUser(null);
      }
      return { success: true, code: response.code, message: response.message };
    } else {
      return { success: false, code: response.code, message: response.message };
    }
  };

  const signup = async (data: SignupData): Promise<{ success: boolean; code?: number; message?: string }> => {
    const response = await apiSignup(data);
    if (response.code >= 200 && response.code < 300 && (response.success || response.user)) {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch {
        setUser(null);
      }
      return { success: true, code: response.code, message: response.message };
    } else {
      return { success: false, code: response.code, message: response.message };
    }
  };

  const logout = async () => {
    setUser(null);
    await apiLogout();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};